import React, { useState, useEffect } from 'react';
import { Modal, Box, Typography, TextField, Button, Grid, IconButton, Chip } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import { LocalizationProvider, DatePicker, TimePicker } from '@mui/x-date-pickers';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import axios from 'axios';
import { APIURL } from '../../../config';
import moment from 'moment';

function DuplicateShiftModal(props) {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    selectedDate: false,
    startTime: false,
    endTime: false,
  });

  const schedule = props.schedule;
  const entityLabelPlural = props.isDayCare ? 'Members' : 'Customers';

  // Get assigned employees/customers from the original shift
  const assignedEmployees = () => {
    return schedule?.employee_ids
      ? props.employees.filter(e => schedule.employee_ids.includes(e.id))
      : [];
  };

  const assignedCustomers = () => {
    const scheduledSessions = props.sessions.filter(s => s.schedule_id === schedule?.schedule_id);
    const customerIds = scheduledSessions.map(s => s.customer_id);
    return props.customers.filter(c => customerIds.includes(c.cus_id));
  };

  useEffect(() => {
    if (!props.open) {
      setSelectedDate(new Date());
      setStartTime(null);
      setEndTime(null);
      setSubmitted(false);
      setLoading(false);
      setErrors({ selectedDate: false, startTime: false, endTime: false });
    }
  }, [props.open]);

  const validate = () => {
    const newErrors = {
      selectedDate: !selectedDate,
      startTime: !startTime || (endTime && moment(startTime).isSameOrAfter(moment(endTime))),
      endTime: !endTime || (startTime && moment(endTime).isSameOrBefore(moment(startTime))),
    };
    setErrors(newErrors);
    setSubmitted(true);
    return Object.values(newErrors).every(e => !e);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    setLoading(true);

    const date = moment(selectedDate);
    const empList = assignedEmployees();
    const cusList = assignedCustomers();

    const session = {
      schedule_name: schedule.schedule_name,
      resource_id: schedule.resource_id,
      employee_ids: empList.map(e => e.id),
      date: date.format('YYYY-MM-DD'),
      start_time: moment(startTime).set({ year: date.year(), month: date.month(), date: date.date() }).utc().format('YYYY-MM-DDTHH:mm:00'),
      end_time: moment(endTime).set({ year: date.year(), month: date.month(), date: date.date() }).utc().format('YYYY-MM-DDTHH:mm:00'),
      max_capacity: schedule.max_capacity,
      task_type: schedule.task_type,
      notes: schedule.notes || null,
      company_id: props.comp_id,
      created_by: props.user.emp_id,
      customers: cusList.map(c => c.cus_id),
      shift: true,
    };

    try {
      const response = await axios.post(APIURL + '/master_schedule', session);
      const schedule_id = response.data.schedule_id;

      axios.post(APIURL + '/shift_action_log', {
        schedule_id,
        shift_emp_id: null,
        action: 'Shift Created',
        notes: `Duplicated from shift: ${schedule.schedule_name}`,
        action_by: props.user.emp_id,
        company_id: props.comp_id,
      }).catch(err => console.error('Error logging shift action:', err));

      empList.forEach(emp => {
        axios.post(APIURL + '/shift_action_log', {
          schedule_id,
          shift_emp_id: emp.id,
          action: 'Employee Assigned',
          notes: null,
          action_by: props.user.emp_id,
          company_id: props.comp_id,
        }).catch(err => console.error('Error logging shift action:', err));
      });

      props.reloadScheduleData();
      props.onClose();
      props.onSuccess('Shift duplicated successfully');
    } catch (error) {
      if (error.response?.status === 400) {
        props.onError(error.response.data.message);
      } else {
        props.onError('An error occurred while duplicating shift');
      }
    } finally {
      setLoading(false);
    }
  };

  const resourceName = props.resources?.find(r => r.resource_id === schedule?.resource_id)?.resource_name || '';

  return (
    <Modal
      enforceFocus={false}
      open={props.open}
      aria-labelledby="duplicate-shift-modal-title"
    >
      <Box sx={{
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: 480, maxWidth: '90%', bgcolor: 'background.paper', boxShadow: 24, p: 4,
        borderRadius: 2, maxHeight: '90vh', overflowY: 'auto'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ContentCopyIcon color="primary" />
            <Typography id="duplicate-shift-modal-title" variant="h6">
              Duplicate Shift
            </Typography>
          </Box>
          <IconButton aria-label="close" onClick={props.onClose} sx={{ p: 0.25 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Original shift info summary */}
        <Box sx={{ bgcolor: '#f5f7fa', borderRadius: 1, p: 2, mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
            Copying from:
          </Typography>
          <Typography variant="body1" fontWeight={600}>
            {schedule?.schedule_name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {schedule?.task_type && <span>{schedule.task_type} · </span>}
            {resourceName}
          </Typography>
          {assignedEmployees().length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">Employees:</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {assignedEmployees().map(e => (
                  <Chip key={e.id} label={e.employee_name} size="small" variant="outlined" />
                ))}
              </Box>
            </Box>
          )}
          {assignedCustomers().length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">{entityLabelPlural}:</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {assignedCustomers().map(c => (
                  <Chip key={c.cus_id} label={c.cus_name} size="small" variant="outlined" />
                ))}
              </Box>
            </Box>
          )}
        </Box>

        {/* New date/time inputs */}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Set new date and times for the duplicate:
        </Typography>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={12}>
                <DatePicker
                  label="Date"
                  value={selectedDate ? new Date(selectedDate) : null}
                  onChange={(val) => setSelectedDate(val)}
                  slotProps={{ textField: { fullWidth: true, error: errors.selectedDate && submitted } }}
                />
              </Grid>
              <Grid size={6}>
                <TimePicker
                  label="Start Time"
                  value={startTime}
                  onChange={setStartTime}
                  slotProps={{ textField: { fullWidth: true, error: errors.startTime && submitted } }}
                />
              </Grid>
              <Grid size={6}>
                <TimePicker
                  label="End Time"
                  value={endTime}
                  onChange={setEndTime}
                  slotProps={{ textField: { fullWidth: true, error: errors.endTime && submitted } }}
                />
              </Grid>
            </Grid>
          </LocalizationProvider>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            startIcon={<ContentCopyIcon />}
          >
            {loading ? 'Duplicating...' : 'Duplicate Shift'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}

export default DuplicateShiftModal;
